import { useDCSSoftwarePlansQuery } from 'hooks/DCS/useDCSSoftwarePlansQuery';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { IBundle, IProduct, ISoftwarePlan } from 'services/dcs/bundle';
import {
  transformBundleForTemplate,
  transformProductForTemplate,
  transformSoftwarePlanPricingForTemplate,
} from 'utils/DcsTemplateTransformUtils';
import { parseDCSTemplateText, DcsObject } from 'utils/ParseDCSTemplateText';
import { useDCSProductsQuery } from './DCS/useDCSProductsQuery';
import { useDCSBundlesQuery } from './DCS/useDCSBundlesQuery';
import { SUBSCRIPTION_STATE, useSubscriptionsState } from './useSubscriptionsState';

enum DCSObjectType {
  PRODUCTS = 'products',
  BUNDLES = 'bundles',
  SOFTWARE_PLANS = 'softwarePlans',
}

export const transformBundlesMap = (
  t: TFunction,
  suscriptionState: SUBSCRIPTION_STATE,
  bundles: IBundle[] | undefined,
) =>
  bundles?.reduce(
    (acc, bundle: IBundle) => ({
      ...acc,
      [bundle.id]: transformBundleForTemplate(t, suscriptionState, bundle),
    }),
    {},
  ) || {};

export const transformProductsMap = (t: TFunction, products: IProduct[] | undefined) =>
  products?.reduce(
    (acc, product: IProduct) => ({
      ...acc,
      [product.id]: transformProductForTemplate(t, product),
    }),
    {},
  ) || {};

export const transformSoftwarePlansMap = (t: TFunction, plans: ISoftwarePlan[] | undefined) =>
  plans?.reduce(
    (acc, plan: ISoftwarePlan) => ({
      ...acc,
      [plan.id]: transformSoftwarePlanPricingForTemplate(t, plan),
    }),
    {},
  ) || {};

/**
 * Accepts an input string that may contain DCS template expressions and returns a processed string with the resolved value of the DCS expressions
 * @param str: input string
 * @returns if string has DCS expressions, returns the parsed string with the resolved value of the DCS expressions. Otherwise, returns the input string as is.
 * @example
 * const dcsText = useDCSText('lorem ${{bundles.HOME_REPAIR_BETTER.pricing.msrp}}')
 * dcsText: '' (until DCS data has loaded)
 * dcsText: 'lorem $1,694' (after DCS data is retrieved)
 *
 * const dcsText = useDCSText('lorem ipsum')
 * dcsText: 'lorem ipsum'
 */
export const useDCSText = (str: string) => {
  const { t } = useTranslation();
  const { data: dcsBundles } = useDCSBundlesQuery();
  const { data: dcsProducts } = useDCSProductsQuery();
  const { data: dcsSoftwarePlans } = useDCSSoftwarePlansQuery();
  const { subscriptionsState } = useSubscriptionsState();

  const dcsObjects = useMemo(() => parseDCSTemplateText(str), [str]);

  const bundlesMapTransformed = useMemo(
    () => transformBundlesMap(t, subscriptionsState, dcsBundles),
    [dcsBundles, t],
  );

  const productsMapTransformed = useMemo(() => transformProductsMap(t, dcsProducts), [dcsProducts, t]);

  const softwarePlanPricingMapTransformed = useMemo(
    () => transformSoftwarePlansMap(t, dcsSoftwarePlans),
    [dcsSoftwarePlans, t],
  );

  const dcsObjectTypeDataMap = {
    [DCSObjectType.BUNDLES]: bundlesMapTransformed,
    [DCSObjectType.PRODUCTS]: productsMapTransformed,
    [DCSObjectType.SOFTWARE_PLANS]: softwarePlanPricingMapTransformed,
  };

  const transformDcsObjects = (dcsObjectsUnprocessed: DcsObject[] | undefined) => {
    if (!dcsObjectsUnprocessed) {
      return {};
    }

    return dcsObjectsUnprocessed.reduce((acc, obj) => {
      const { dcsObjectType, dcsID, dcsObjectPath, matchedValue } = obj;
      const dcsObject = dcsObjectTypeDataMap[dcsObjectType]?.[dcsID];

      if (!dcsObject) {
        acc[matchedValue] = '';
        return acc;
      }

      const paths = dcsObjectPath.split('.');
      const nestedValue = paths.reduce(
        (currentObject, currentPath) => currentObject?.[currentPath],
        dcsObject,
      );

      acc[matchedValue] = nestedValue ?? '';
      return acc;
    }, {});
  };

  const dcsTextTemplateData = useMemo(
    () => transformDcsObjects(dcsObjects),
    [dcsObjects, bundlesMapTransformed, productsMapTransformed, softwarePlanPricingMapTransformed],
  );

  const dcsText = useMemo(() => {
    if (!dcsObjects || !str || Object.values(dcsTextTemplateData).some(item => item === undefined)) {
      return '';
    }

    return dcsObjects.reduce(
      (result, { matchedValue }) =>
        // replaces matchedValue in `result` with the value from `dcsTextTemplateData`
        result.replace(matchedValue, dcsTextTemplateData[matchedValue]),
      str,
    );
  }, [str, dcsObjects, dcsTextTemplateData]);

  return dcsObjects ? dcsText : str;
};
